/**
 * Global Image Studio command handlers.
 *
 * Registered once at app startup (in commandRegistryStore.initializeAllCommands).
 * Previously these lived inside a useEffect in ImageStudio.tsx, which:
 *   - Required a window to be mounted before the handler could run.
 *   - Overwrote each other when two ImageStudio windows were open.
 *   - Closure-captured a stale spaceId and never unregistered.
 *
 * The global handlers route into every open Image Studio for the target
 * space via pushAgent{Progress,Result,Failure}.
 */
import {
  pushAgentProgress,
  pushAgentResult,
  pushAgentFailure,
} from '../store/imageStudioStore';
import { FILES_ENDPOINTS } from '../../../endpoints';
import { AIOperationMode } from '../store/types';
import { createFileLogger } from '../../../utils/logger';

const log = createFileLogger('IMAGE_STUDIO');

type VariantPayload = { entityId?: string; name?: string; imageUrl?: string };

/**
 * image_studio_progress — emitted at tool start so the VariantsStrip can show
 * a pending placeholder while DALL-E runs. Also used with status='failed'
 * to clear the spinner on errors.
 */
export const imageStudioProgressCommand = {
  id: 'image_studio_progress',
  description: 'Receive AI-agent image-tool progress into Image Studio',
  handler: async (params: any) => {
    const { opId, status, spaceId, prompt, mode, error } = params ?? {};
    if (!spaceId || !opId) return { status: 'skipped', reason: 'missing spaceId or opId' };
    const effectiveMode: AIOperationMode = mode ?? 'edit';
    if (status === 'failed') {
      pushAgentFailure(spaceId, opId, error ?? 'Agent edit failed');
    } else {
      pushAgentProgress(spaceId, opId, prompt ?? '…', effectiveMode);
    }
    log.log('[cmd] image_studio_progress', { opId, status, spaceId, mode: effectiveMode });
    return { status: 'success' };
  },
};

/**
 * image_studio_result — emitted when an agent tool (image_edit_region,
 * image_generate, image_remove_bg) finishes. Payload contains entity IDs
 * (the actual image bytes are in S3 via persist-variant), which we resolve
 * to fetchable URLs via FILES_ENDPOINTS.read().
 */
export const imageStudioResultCommand = {
  id: 'image_studio_result',
  description: 'Receive AI-agent image edit / generation result into Image Studio',
  handler: async (params: any) => {
    const { opId, spaceId, prompt, mode, entityId, name, variants } = params ?? {};
    if (!spaceId) return { status: 'skipped', reason: 'missing spaceId' };

    const effectiveMode: AIOperationMode = mode ?? 'edit';

    // Normalise to a variant array. Supports three shapes:
    //   1. { variants: [{entityId, name}, ...] }          (multi-variant generate)
    //   2. { entityId, name }                              (single-variant edit/bg)
    //   3. { imageData }                                   (legacy inline base64 —
    //                                                      still handled for back-compat
    //                                                      but discouraged; see Bug 2
    //                                                      notes: 512KB WS body cap).
    const out: Array<{ imageUrl: string; entityId?: string; name?: string }> = [];

    const raw: VariantPayload[] = Array.isArray(variants) && variants.length > 0
      ? variants
      : entityId
        ? [{ entityId, name }]
        : [];

    for (const v of raw) {
      if (v.entityId) {
        out.push({
          imageUrl: FILES_ENDPOINTS.read(v.entityId),
          entityId: v.entityId,
          name: v.name,
        });
      } else if (v.imageUrl) {
        out.push({ imageUrl: v.imageUrl, name: v.name });
      }
    }

    // Legacy back-compat path — inline base64 from older tool versions.
    if (out.length === 0 && typeof params?.imageData === 'string' && params.imageData.length > 0) {
      out.push({ imageUrl: params.imageData });
    }

    if (out.length === 0) {
      return { status: 'error', error: 'No variants in image_studio_result payload' };
    }

    const pushed = pushAgentResult(spaceId, opId ?? null, out, prompt ?? 'Agent Edit', effectiveMode);
    log.log('[cmd] image_studio_result', { opId, spaceId, variantCount: out.length, pushed, mode: effectiveMode });
    return { status: 'success', pushedTo: pushed };
  },
};

export const IMAGE_STUDIO_COMMANDS = [
  imageStudioProgressCommand,
  imageStudioResultCommand,
];
